import React from 'react';
import { motion, useScroll, useTransform, useMotionValue } from 'motion/react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { mascot, appScreenshots } from '../../data/mock';
import { STORE_LINKS } from '../../constants/links';

const HeroSection = () => {
  const { t } = useTranslation();

  // Scroll Parallax Elements
  const { scrollY } = useScroll();
  const textY = useTransform(scrollY, [0, 500], [0, 80]);
  const mockupsY = useTransform(scrollY, [0, 500], [0, -40]);

  // 3D Tilt Effect on mouse movement
  const tiltX = useMotionValue(0);
  const tiltY = useMotionValue(0);
  const rotateX = useTransform(tiltY, [-150, 150], [10, -10]);
  const rotateY = useTransform(tiltX, [-150, 150], [-10, 10]);

  const handleMouseMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = event.clientX - rect.left - width / 2;
    const mouseY = event.clientY - rect.top - height / 2;
    tiltX.set(mouseX);
    tiltY.set(mouseY);
  };

  const handleMouseLeave = () => {
    tiltX.set(0);
    tiltY.set(0);
  };

  return (
    <section className="relative min-h-screen overflow-hidden bg-white flex items-center justify-center pt-28 lg:pt-36 pb-20 z-10 select-none">
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 z-10 w-full">
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          
          {/* Left Content: Headlines & Walking Mascot (7 cols) */}
          <motion.div
            style={{ y: textY }}
            className="lg:col-span-7 text-left flex flex-col justify-center"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Minimalist Badge */}
            <div
              className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200/80 rounded-full px-4 py-1.5 mb-6 self-start shadow-sm"
            >
              <Sparkles className="w-3.5 h-3.5 text-slate-800" />
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-800">
                {t('hero.badge')}
              </span>
            </div>

            {/* Giant Title (No Gradients, solid black, tracking close) with Masked Slide-up */}
            <h1 className="text-5xl sm:text-6xl lg:text-[5.5rem] font-black text-slate-950 leading-[1.02] mb-6 tracking-tighter">
              <span className="block overflow-hidden py-1">
                <motion.span
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
                  className="inline-block"
                >
                  {t('hero.title1')}
                </motion.span>
              </span>
              <span className="block overflow-hidden py-1">
                <motion.span
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
                  className="inline-block"
                >
                  {t('hero.title2')}
                </motion.span>
              </span>
            </h1>

            {/* Paragraph description */}
            <p className="text-slate-500 text-base lg:text-lg mb-10 max-w-md leading-relaxed font-semibold">
              {t('hero.description')}
            </p>

            {/* Walking Mascot GIF Container */}
            <div className="mb-10 flex items-center justify-start">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="w-36 h-36 bg-[#FAF9F6] border border-slate-200 rounded-3xl p-3 flex items-center justify-center shadow-sm relative overflow-hidden group"
              >
                <img
                  src={process.env.PUBLIC_URL + "/assets/monito_caminando.gif"}
                  alt="Monito caminando"
                  className="w-full h-full object-contain"
                  draggable={false}
                />
              </motion.div>
            </div>

            {/* Store Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <motion.a
                href={STORE_LINKS.ios}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98 }}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-4 text-xs font-black shadow-sm uppercase tracking-wider flex items-center justify-center gap-2 border-none"
              >
                {t('hero.appStore')} <ArrowRight className="w-4 h-4" />
              </motion.a>
              <motion.a
                href={STORE_LINKS.android}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98 }}
                className="bg-white text-slate-900 border border-slate-200 hover:border-slate-300 rounded-full px-8 py-4 text-xs font-black uppercase tracking-wider flex items-center justify-center shadow-sm"
              >
                {t('hero.googlePlay')}
              </motion.a>
            </div>
          </motion.div>

          {/* Right Content: 3D Interactive Phone Mockups (5 cols) */}
          <motion.div 
            style={{ y: mockupsY }}
            className="lg:col-span-5 relative flex justify-center lg:justify-end"
          >
            <motion.div
              style={{
                rotateX,
                rotateY,
                transformStyle: "preserve-3d",
                perspective: "1000px"
              }}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              className="relative cursor-grab active:cursor-grabbing"
            >
              {/* Main Phone */}
              <motion.div
                className="relative z-20 w-60 sm:w-64 lg:w-[17.5rem]"
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                style={{ transform: "translateZ(30px)" }}
              >
                <div className="bg-slate-100 rounded-[2.8rem] p-2 shadow-xl border border-slate-200/60">
                  <div className="bg-white rounded-[2.3rem] overflow-hidden relative border border-slate-100">
                    <img
                      src={appScreenshots.map}
                      alt="APEKAI Places Map screen"
                      className="w-full h-auto object-cover"
                      draggable={false}
                    />
                  </div>
                </div>
              </motion.div>

              {/* Secondary Phone (Left-behind) */}
              <motion.div
                className="absolute -left-14 sm:-left-16 top-24 z-10 w-40 sm:w-44 lg:w-48 hidden sm:block"
                initial={{ opacity: 0, x: -20, rotate: -5 }}
                animate={{ opacity: 0.85, x: 0, rotate: -5, y: [0, -4, 0] }}
                transition={{
                  duration: 0.8,
                  delay: 0.4,
                  y: { duration: 7, repeat: Infinity, ease: "easeInOut", delay: 0.5 }
                }}
                style={{ transform: "translateZ(10px)" }}
              >
                <div className="bg-slate-100 rounded-[2.2rem] p-1.5 shadow-lg border border-slate-200/60">
                  <div className="bg-white rounded-[1.6rem] overflow-hidden border border-slate-100">
                    <img
                      src={appScreenshots.placesList}
                      alt="APEKAI Places List screen"
                      className="w-full h-auto object-cover"
                      draggable={false}
                    />
                  </div>
                </div>
              </motion.div>

              {/* Floating Monito Assistant Badge */}
              <motion.div
                className="absolute -right-8 bottom-16 z-30 w-22 h-22"
                animate={{
                  y: [0, -4, 0],
                  rotate: [0, 3, -3, 0]
                }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                style={{ transform: "translateZ(50px)" }}
              >
                <img
                  src={mascot.traveler}
                  alt="Mascot travel"
                  className="w-full h-full object-contain drop-shadow-lg"
                  draggable={false}
                />
              </motion.div>
            </motion.div>
          </motion.div>
          
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
